// src/utils/useSignOut.tsx
//
// Odhlášení s potvrzením – sdílí ho Profil (UserProfilePage) a účet
// v hlavičce (ui/AccountMenu). Postup i texty jsou beze změny převzaté
// z dřívějšího handleLogout v UserProfilePage.
//
// Bezpečnost (a): po signOut se ukončí Firestore a vymaže jeho cache
// v zařízení – na sdíleném telefonu nebo PC v ní jinak zůstanou profily
// a přihlášky z této relace. Stránka se pak načte znovu (čistý Firestore).
// Neodeslané zápisy (offline) by vymazání zahodilo – dřív se počká na jejich
// odeslání a když nedojdou, odhlášení se potvrzuje znovu.
//
// Běžící odhlášení je jedno pro celou aplikaci (stav modulu, ne komponenty):
// na obrazovce Profil je tlačítko Odhlásit v seznamu i v dialogu účtu,
// druhé spuštění během prvního se proto ignoruje a obě místa ukazují
// „Odhlašuji…".

import React, { useCallback, useSyncExternalStore } from "react";
import { useNavigate } from "react-router-dom";
import { signOut as firebaseSignOut } from "firebase/auth";
import {
  auth,
  clearFirestoreCache,
  pendingWritesSettled,
  restartApp,
} from "../firebase";
import { useConfirm } from "../ui/ConfirmDialog";
import { useToast } from "../ui/Toast";
import { firebaseErrorMessage } from "./firebaseErrors";
import { reportError } from "./reportError";

let running = false;
const listeners = new Set<() => void>();

function setRunning(value: boolean): void {
  running = value;
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

const isRunning = (): boolean => running;

export interface SignOutControl {
  /** Spustí odhlášení (potvrzení → odhlášení → /login a nové načtení). Nevyhazuje. */
  signOut: () => Promise<void>;
  /** Odhlašování běží – tlačítka ukazují „Odhlašuji…". */
  signingOut: boolean;
}

/**
 * Odhlášení s potvrzením. `where` je místo pro diagnostiku chyby odhlášení
 * (reportError "<where>.logout", např. "UserProfilePage").
 */
export function useSignOut(where: string): SignOutControl {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { showToast } = useToast();
  const signingOut = useSyncExternalStore(subscribe, isRunning);

  const signOut = useCallback(async () => {
    if (running) return;
    const ok = await confirm({
      title: "Odhlásit se?",
      message: "Na tomto zařízení budete z aplikace odhlášeni.",
      confirmLabel: "Odhlásit se",
    });
    if (!ok || running) return;

    setRunning(true);
    if (!(await pendingWritesSettled())) {
      const force = await confirm({
        title: "Neodeslané změny",
        tone: "danger",
        message:
          "Některé změny (např. zapsané výsledky) se ještě neodeslaly na server – zařízení je nejspíš offline. Odhlášením se ztratí.",
        details: (
          <p className="mb-0">
            Počkejte na připojení a odhlaste se znovu, nebo se odhlaste přesto.
          </p>
        ),
        confirmLabel: "Odhlásit přesto",
      });
      if (!force) {
        setRunning(false);
        return;
      }
    }
    try {
      await firebaseSignOut(auth);
    } catch (err) {
      setRunning(false);
      reportError(`${where}.logout`, err);
      showToast(
        firebaseErrorMessage(err, "Odhlášení se nepodařilo. Zkuste to prosím znovu."),
        "error"
      );
      return;
    }
    navigate("/login", { replace: true });
    // Chyby úklidu cache se jen zapíšou do diagnostiky (clearFirestoreCache
    // nevyhazuje); nové načtení stránky proběhne vždy (offline až po
    // obnovení spojení).
    await clearFirestoreCache();
    restartApp("/login");
    // Stránka se načítá znovu (offline až po připojení – uživatel je mezitím
    // odhlášený na /login); postup tím skončil.
    setRunning(false);
  }, [confirm, navigate, showToast, where]);

  return { signOut, signingOut };
}

export default useSignOut;
